import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { centsToDollar } from "utils";

import {
  usePaymentMethods,
  useSetDefaultPaymentMethod,
  PaymentMethodRow,
  PAYMENT_METHODS_QUERY_KEY,
} from "hooks/api/usePaymentMethods";
import { useAddresses, ADDRESSES_QUERY_KEY } from "hooks/api/useAddresses";
import {
  useCompanySubscription,
  useSubscriptionPlans,
  useCreateSubscription,
  useUpsertBillingProfile,
} from "hooks/api";
import { useSnackbar } from "contexts/SnackbarContext";
import { Box, Divider, Grid, Stack, Typography } from "@mui/material";
import { Button } from "@repo/ui";
import type { Address } from "types";

import { Header, EmptyState, Loader } from "components";
import { CreatePaymentMethodModal, CreateAddressModal } from "components/feedback/modals";
import { CheckoutShippingRow } from "pages/main/TagsShopCheckoutPage/components/CheckoutShippingRow";
import { CheckoutPaymentRow } from "pages/main/TagsShopCheckoutPage/components/CheckoutPaymentRow";
import { ChangeAddressModal } from "pages/main/TagsShopCheckoutPage/components/ChangeAddressModal";
import { ChangePaymentModal } from "pages/main/TagsShopCheckoutPage/components/ChangePaymentModal";
import { ROUTES } from "routes";

export const BillingPlanCheckoutPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const priceId = searchParams.get("priceId");
  const planId = searchParams.get("planId");

  const { open: notify } = useSnackbar();

  const { data: subscription } = useCompanySubscription(null, true);
  const { data: plans = [], isLoading: isLoadingPlans } = useSubscriptionPlans();
  const {
    data: paymentMethods,
    isLoading: isLoadingPaymentMethods,
  } = usePaymentMethods();
  const { addresses, isLoading: isLoadingAddresses } = useAddresses();
  const setDefaultPaymentMethod = useSetDefaultPaymentMethod();
  const createSubscription = useCreateSubscription();
  const upsertBillingProfile = useUpsertBillingProfile();

  // Selection state — mirrors the tag-shop checkout: explicit choice → default → first.
  const [chosenAddressId, setChosenAddressId] = useState<string | null>(null);
  const [chosenMethodId, setChosenMethodId] = useState<string | null>(null);
  const [openCreatePaymentMethod, setOpenCreatePaymentMethod] = useState(false);
  const [openAddAddress, setOpenAddAddress] = useState(false);
  const [openChangeAddress, setOpenChangeAddress] = useState(false);
  const [openChangePayment, setOpenChangePayment] = useState(false);

  const plan = useMemo(() => {
    if (planId) {
      const found = plans.find((item) => item.id === planId);
      if (found) return found;
    }
    if (priceId) {
      const found = plans.find((item) => item.stripe_price_id === priceId);
      if (found) return found;
    }
    return subscription?.plan ?? null;
  }, [planId, priceId, plans, subscription?.plan]);

  const planPrice = plan ? centsToDollar(plan.amount) : "—";
  const planInterval = plan?.interval ?? subscription?.plan?.interval ?? "month";

  // Use the raw saved methods (same as the tag-shop checkout). Don't synthesize a
  // `label` from brand+last4 — the row/picker already render "Visa •••• 4242" as
  // the subtitle, so overriding the label duplicated it as the title.
  const paymentOptions = useMemo<PaymentMethodRow[]>(
    () => (paymentMethods?.methods ?? []) as PaymentMethodRow[],
    [paymentMethods],
  );

  // Resolve the selected address/card (explicit choice → default → first).
  const selectedAddress = useMemo<Address | null>(() => {
    if (!addresses.length) return null;
    return (
      addresses.find((a) => a.id === chosenAddressId) ??
      addresses.find((a) => a.is_default) ??
      addresses[0]
    );
  }, [addresses, chosenAddressId]);

  const selectedMethod = useMemo<PaymentMethodRow | null>(() => {
    if (!paymentOptions.length) return null;
    return (
      paymentOptions.find((m) => m.id === chosenMethodId) ??
      paymentOptions.find((m) => m.isDefault) ??
      paymentOptions[0]
    );
  }, [paymentOptions, chosenMethodId]);

  const isLoading = isLoadingPlans || isLoadingAddresses || isLoadingPaymentMethods;

  const handlePay = async () => {
    if (!plan || !priceId) {
      notify({
        type: "error",
        title: "Plan not found",
        text: "Please go back and choose a subscription plan.",
      });
      return;
    }
    if (!selectedMethod) {
      notify({
        type: "error",
        title: "No payment method",
        text: "Please add or select a payment method before continuing.",
      });
      return;
    }
    if (!selectedAddress) {
      notify({
        type: "error",
        title: "Billing address required",
        text: "Please add a billing address before continuing.",
      });
      return;
    }

    try {
      // Persist the chosen address as the Stripe customer's billing address. The
      // billing profile only knows store/production/other, so map any other
      // address type (e.g. the new "office") to "other".
      const billingType =
        selectedAddress.type === "store" ||
        selectedAddress.type === "production"
          ? selectedAddress.type
          : "other";
      await upsertBillingProfile.mutateAsync({
        type: billingType,
        label: selectedAddress.label ?? null,
        fullName: selectedAddress.full_name || selectedAddress.label || "",
        addressLine: selectedAddress.address_line,
        city: selectedAddress.city ? selectedAddress.city : null,
        state: selectedAddress.state ? selectedAddress.state : null,
        postalCode: selectedAddress.postal_code,
        country: selectedAddress.country,
      });

      if (!selectedMethod.isDefault) {
        await setDefaultPaymentMethod.mutateAsync(selectedMethod.id);
      }
      await createSubscription.mutateAsync({ priceId });
      notify({
        type: "success",
        title: "Subscription updated",
        text: "Your plan change is now active.",
      });
      navigate(ROUTES.SETTINGS_COMPANY_BILLING_PLAN);
    } catch (error: any) {
      notify({
        type: "error",
        title: "Payment failed",
        text: error?.message ?? "Unable to update the subscription.",
      });
    }
  };

  if (!plan && !isLoading) {
    return (
      <EmptyState
        title="Plan not found"
        text="We could not find the selected plan. Please go back and choose your plan again."
        buttons={
          <Button
            variant="primary"
            onClick={() => navigate(ROUTES.SETTINGS_COMPANY_BILLING_PLAN)}
          >
            Back to plans
          </Button>
        }
      />
    );
  }

  if (isLoading) {
    return <Loader />;
  }

  const isProcessing =
    createSubscription.isPending || upsertBillingProfile.isPending;

  return (
    <>
      <Header title="Checkout" breadcrumbsOveride={"Checkout"} />
      {/* Same layout + components as the tag-shop checkout: a wide left card
          (md=8) with the address + payment rows separated by a divider, and a
          sticky summary (md=4). Only the summary + pay handler differ. */}
      <Grid container spacing={6} alignItems="flex-start">
        <Grid item xs={12} md={8}>
          <Box
            sx={{
              backgroundColor: "background.paper",
              border: (theme) => `1px solid ${theme.palette.primary[100]}`,
              borderRadius: 2,
            }}
          >
            <Stack spacing={4} p={6}>
              <CheckoutShippingRow
                title="Billing address"
                address={selectedAddress}
                isLoading={isLoadingAddresses}
                onChange={() => setOpenChangeAddress(true)}
                onAdd={() => setOpenAddAddress(true)}
              />

              <Divider />

              <CheckoutPaymentRow
                method={selectedMethod}
                isLoading={isLoadingPaymentMethods}
                onChange={() => setOpenChangePayment(true)}
                onAdd={() => setOpenCreatePaymentMethod(true)}
              />
            </Stack>
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box
            sx={{
              width: "100%",
              backgroundColor: "background.paper",
              border: (theme) => `1px solid ${theme.palette.primary[100]}`,
              borderRadius: 2,
              position: "sticky",
              top: 24,
            }}
          >
            <Stack spacing={4} p={6}>
              <Typography variant="h6">Checkout summary</Typography>

              <Stack spacing={3}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  spacing={3}
                >
                  <Stack minWidth={0}>
                    <Typography variant="body2">
                      {plan?.title ?? "Selected plan"}
                    </Typography>
                    <Typography variant="body3" color="text.secondary">
                      {`Billed ${planInterval === "year" ? "yearly" : "monthly"}`}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ flexShrink: 0 }}>
                    {planPrice}
                  </Typography>
                </Stack>
              </Stack>

              <Divider />

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="body1">Total</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {planPrice}
                </Typography>
              </Stack>

              <Button
                variant="primary"
                fullWidth
                loading={isProcessing}
                disabled={!plan || !selectedMethod || isProcessing}
                onClick={handlePay}
              >
                {`Pay ${plan ? planPrice : ""}`}
              </Button>

              <Typography
                variant="body3"
                color="text.secondary"
                textAlign="center"
              >
                Want to unsubscribe? You can cancel your subscription at any
                time.
              </Typography>
            </Stack>
          </Box>
        </Grid>
      </Grid>

      {/* Address: same picker + add-new modal as the tag-shop checkout. */}
      <ChangeAddressModal
        open={openChangeAddress}
        onClose={() => setOpenChangeAddress(false)}
        addresses={addresses}
        selectedId={selectedAddress?.id ?? null}
        onSelect={(address) => setChosenAddressId(address.id)}
        onAddNew={() => {
          setOpenChangeAddress(false);
          setOpenAddAddress(true);
        }}
      />
      <CreateAddressModal
        open={openAddAddress}
        onClose={() => setOpenAddAddress(false)}
        refetch={() =>
          queryClient.invalidateQueries({ queryKey: ADDRESSES_QUERY_KEY })
        }
      />

      {/* Payment: same picker + add-new modal as the tag-shop checkout. */}
      <ChangePaymentModal
        open={openChangePayment}
        onClose={() => setOpenChangePayment(false)}
        methods={paymentOptions}
        selectedId={selectedMethod?.id ?? null}
        onSelect={(method) => setChosenMethodId(method.id)}
        onAddNew={() => {
          setOpenChangePayment(false);
          setOpenCreatePaymentMethod(true);
        }}
      />
      <CreatePaymentMethodModal
        open={openCreatePaymentMethod}
        onClose={() => setOpenCreatePaymentMethod(false)}
        refetch={() =>
          queryClient.invalidateQueries({ queryKey: PAYMENT_METHODS_QUERY_KEY })
        }
      />
    </>
  );
};
